import { useState } from "react";

import { Combobox, DropdownButton } from "@similarweb/ui-components";

import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import { StyledBaseDropdownButtonWrap } from "app/pages/sales-intelligence/common-components/styles";

import type { IFilterViewBaseModelProps } from "../../../types/filterInstance";
import type { DynamicComboboxFilterOption, DynamicComboboxFilterOptions } from "../../../types/filterOptions";
import { isDynamicListSearchActive } from "../../../utils/search";

import SearchableComboboxContent from "./SearchableComboboxContent";

const EnumComboboxFilter = ({
    filter: {
        options: { options, label, placeholder },
    },
    value,
    disabledOptionIds,
    isTopLayer,
    onFilterUpdate,
}: IFilterViewBaseModelProps<string, DynamicComboboxFilterOptions>) => {
    const [isOpen, setIsOpen] = useState(false);

    const isOptionDisabled = (id: string): boolean => !!disabledOptionIds?.includes(id) && id !== value;

    const selectedLabel = options.find((option) => option.id === value)?.label ?? placeholder;

    const handleSelect = (id: string) => {
        onFilterUpdate(id);
        setIsOpen(false);
    };

    const renderOption = (option: DynamicComboboxFilterOption) => (
        <Combobox.Option
            key={option.id}
            id={option.id}
            textValue={option.label}
            isSelected={option.id === value}
            isDisabled={isOptionDisabled(option.id)}
            onClick={() => !isOptionDisabled(option.id) && handleSelect(option.id)}
        >
            <Combobox.OptionLabel>{option.label}</Combobox.OptionLabel>
        </Combobox.Option>
    );

    const combobox = (
        <Combobox
            data-automation="enum-combobox-filter"
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            {...(isTopLayer ? { "data-react-aria-top-layer": "true" } : {})}
        >
            <Combobox.Trigger>
                <StyledBaseDropdownButtonWrap>
                    <DropdownButton hasValue={Boolean(value)}>{selectedLabel}</DropdownButton>
                </StyledBaseDropdownButtonWrap>
            </Combobox.Trigger>

            {isDynamicListSearchActive(options) ? (
                <SearchableComboboxContent
                    isOpen={isOpen}
                    options={options}
                    searchDataAutomation="enum-combobox-filter-search"
                    renderOption={renderOption}
                />
            ) : (
                <Combobox.Content>{options.map(renderOption)}</Combobox.Content>
            )}
        </Combobox>
    );

    return label ? <FormFieldLabel text={label}>{combobox}</FormFieldLabel> : combobox;
};

export default EnumComboboxFilter;
